import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { attendanceAPI } from '../services/api';
import { Worker } from '../types';
import { showCustomAlert } from './CustomAlert';

type WorkerPeriod = {
  workerId: string;
  inSickLeavePeriod: boolean;
  sickLeavePeriod?: { startDate: string; endDate?: string | null; diagnosis?: string; isActive?: boolean } | null;
  inAnnualLeavePeriod: boolean;
  annualLeavePeriod?: { startDate: string; endDate: string; leaveType: 'old' | 'new'; numberOfDays: number } | null;
  inPaidUnpaidLeavePeriod: boolean;
  paidUnpaidLeavePeriod?: { startDate: string; endDate: string; leaveType: 'paid' | 'unpaid'; numberOfDays: number; reason: string } | null;
};

type DailyRecordResponse = {
  _id: string | null;
  workerId: any; // populated Worker-like
  date: string;
  hoursWorked: number;
  overtimeHours: number;
  isAnnualLeave: boolean;
  isSickLeave: boolean;
  isSlava: boolean;
  notes?: string;
  // extra flags from backend
  inSickLeavePeriod?: boolean;
  inAnnualLeavePeriod?: boolean;
  inPaidUnpaidLeavePeriod?: boolean;
  isHoliday?: boolean;
  holidayName?: string | null;
};

type RowState = {
  worker: Worker;
  recordId: string | null;
  locked: boolean;
  cannotUnlock: boolean;
  isHoliday: boolean;
  holidayName?: string | null;

  hoursWorked: number;
  overtimeHours: number;
  isAnnualLeave: boolean;
  isSickLeave: boolean;
  notes: string;

  dirty: boolean;
  saving: boolean;
};

function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

const AttendanceDaily: React.FC = () => {
  const [date, setDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const [rows, setRows] = useState<RowState[]>([]);

  const refresh = async () => {
    setLoading(true);
    try {
      const dateKey = ymd(date);
      const [workers, daily, periods] = await Promise.all([
        attendanceAPI.getWorkers({ isActive: true }),
        attendanceAPI.getDailyRecords(dateKey) as Promise<DailyRecordResponse[]>,
        attendanceAPI.getWorkerPeriodsForDate(dateKey) as Promise<WorkerPeriod[]>
      ]);

      const periodMap = new Map<string, WorkerPeriod>();
      periods.forEach((p) => periodMap.set(p.workerId, p));

      const recordMap = new Map<string, DailyRecordResponse>();
      daily.forEach((r) => {
        const wid = typeof r.workerId === 'object' && r.workerId?._id ? String(r.workerId._id) : String(r.workerId);
        recordMap.set(wid, r);
      });

      const next: RowState[] = workers.map((w) => {
        const wid = w._id;
        const rec = recordMap.get(wid);
        const per = periodMap.get(wid);

        const inPeriod =
          !!per?.inSickLeavePeriod || !!per?.inAnnualLeavePeriod || !!per?.inPaidUnpaidLeavePeriod;

        // Base values
        let hoursWorked = rec?.hoursWorked ?? 8;
        let overtimeHours = rec?.overtimeHours ?? 0;
        let isAnnualLeave = rec?.isAnnualLeave ?? false;
        let isSickLeave = rec?.isSickLeave ?? false;
        let notes = rec?.notes ?? '';
        const isHoliday = !!rec?.isHoliday;
        const holidayName = rec?.holidayName ?? null;

        // If worker is in an active period from details, force locked + mark flags (cannot unlock)
        if (per?.inSickLeavePeriod) {
          isSickLeave = true;
          isAnnualLeave = false;
          hoursWorked = 8;
          overtimeHours = 0;
        } else if (per?.inAnnualLeavePeriod) {
          isAnnualLeave = true;
          isSickLeave = false;
          hoursWorked = 8;
          overtimeHours = 0;
        } else if (per?.inPaidUnpaidLeavePeriod) {
          // daily UI doesn't have a dedicated column; lock the row anyway
          const leaveType = per.paidUnpaidLeavePeriod?.leaveType;
          hoursWorked = leaveType === 'paid' ? 8 : 0;
          overtimeHours = 0;
          isAnnualLeave = false;
          isSickLeave = false;
        }

        // If holiday pseudo-record exists, lock row
        const locked = !!rec?._id || isHoliday || inPeriod;
        const cannotUnlock = isHoliday || inPeriod;

        return {
          worker: w,
          recordId: rec?._id ?? null,
          locked,
          cannotUnlock,
          isHoliday,
          holidayName,
          hoursWorked,
          overtimeHours,
          isAnnualLeave,
          isSickLeave,
          notes,
          dirty: !locked, // default: unsaved rows show "Čeka sačuvaj"
          saving: false
        };
      });

      setRows(next);
    } catch (e: any) {
      console.error('Error loading daily attendance:', e);
      showCustomAlert.error(e?.response?.data?.message || 'Greška pri učitavanju dnevne evidencije');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymd(date)]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const w = r.worker;
      const full = `${w.firstName} ${w.lastName}`.toLowerCase();
      const rev = `${w.lastName} ${w.firstName}`.toLowerCase();
      const id = (w.employeeId || '').toLowerCase();
      const pos =
        typeof w.positionId === 'object' && w.positionId ? (w.positionId as any).name?.toLowerCase?.() : '';
      return full.includes(q) || rev.includes(q) || id.includes(q) || pos.includes(q);
    });
  }, [rows, query]);

  const updateRow = (workerId: string, patch: Partial<RowState>) => {
    setRows((prev) =>
      prev.map((r) => (r.worker._id === workerId ? { ...r, ...patch, dirty: patch.dirty ?? true } : r))
    );
  };

  const handleToggleAnnual = (r: RowState, checked: boolean) => {
    if (r.locked) return;
    // Annual means 1 day = 8h
    updateRow(r.worker._id, {
      isAnnualLeave: checked,
      isSickLeave: checked ? false : r.isSickLeave,
      hoursWorked: checked ? 8 : r.hoursWorked,
      overtimeHours: checked ? 0 : r.overtimeHours,
      dirty: true
    });
  };

  const handleToggleSick = (r: RowState, checked: boolean) => {
    if (r.locked) return;
    updateRow(r.worker._id, {
      isSickLeave: checked,
      isAnnualLeave: checked ? false : r.isAnnualLeave,
      hoursWorked: checked ? 8 : r.hoursWorked,
      overtimeHours: checked ? 0 : r.overtimeHours,
      dirty: true
    });
  };

  const saveRow = async (r: RowState) => {
    try {
      // Validation: overtime increments of 0.5; minimum 0.5 if > 0
      if (r.overtimeHours > 0 && r.overtimeHours < 0.5) {
        showCustomAlert.warning('Minimum za prekovremeni rad je 0.5 sata.');
        return;
      }
      if (r.overtimeHours % 0.5 !== 0) {
        showCustomAlert.warning('Prekovremeni sati moraju biti u intervalima od 0.5 sata.');
        return;
      }

      updateRow(r.worker._id, { saving: true });

      const dateKey = ymd(date);
      const payload = {
        workerId: r.worker._id,
        // Use YYYY-MM-DD string; backend normalizes to start of day
        date: dateKey,
        hoursWorked: r.isAnnualLeave || r.isSickLeave ? 8 : clamp(r.hoursWorked, 0, 24),
        overtimeHours: r.isAnnualLeave || r.isSickLeave ? 0 : clamp(r.overtimeHours, 0, 24),
        isAnnualLeave: !!r.isAnnualLeave,
        isSickLeave: !!r.isSickLeave,
        isSlava: false,
        notes: r.notes || ''
      };

      const saved = await attendanceAPI.upsertAttendanceRecord(payload);

      updateRow(r.worker._id, {
        recordId: saved?._id ?? r.recordId,
        locked: true,
        saving: false,
        dirty: false
      });
    } catch (e: any) {
      console.error('Error saving record:', e);
      updateRow(r.worker._id, { saving: false });
      showCustomAlert.error(e?.response?.data?.message || 'Greška pri čuvanju evidencije');
    }
  };

  const unlockRow = (r: RowState) => {
    if (!r.locked) return;
    if (r.cannotUnlock) {
      showCustomAlert.warning('Ne može da se otključa dok ne istekne period (bolovanje/godišnji/odsustvo/praznik).');
      return;
    }
    updateRow(r.worker._id, { locked: false, dirty: false });
  };

  const saveAll = async () => {
    confirmDialog({
      header: 'Sačuvaj sve',
      icon: 'pi pi-exclamation-triangle',
      message:
        'Ovo će sačuvati 8h redovnog rada za sve radnike koji nemaju današnji unos (ne dira radnike u periodu bolovanja/godišnjeg/odsustva).',
      acceptLabel: 'Sačuvaj sve',
      rejectLabel: 'Otkaži',
      accept: async () => {
        const dateKey = ymd(date);
        const targets = rows.filter((r) => !r.recordId && !r.cannotUnlock); // no record and not locked by period/holiday
        if (targets.length === 0) {
          showCustomAlert.info?.('Nema radnika za čuvanje.') as any;
          return;
        }
        setLoading(true);
        try {
          await Promise.allSettled(
            targets.map((r) =>
              attendanceAPI.upsertAttendanceRecord({
                workerId: r.worker._id,
                date: dateKey,
                hoursWorked: 8,
                overtimeHours: 0,
                isAnnualLeave: false,
                isSickLeave: false,
                isSlava: false,
                notes: ''
              })
            )
          );
          showCustomAlert.success('Sačuvano za sve radnike bez unosa');
          await refresh();
        } catch (e: any) {
          showCustomAlert.error(e?.response?.data?.message || 'Greška pri čuvanju svih zapisa');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const cleanupOrphaned = async () => {
    confirmDialog({
      header: 'Očisti napuštene zapise',
      icon: 'pi pi-exclamation-triangle',
      message: 'Ovo će obrisati zapise sa null workerId (napuštene zapise) iz evidencije i istorija.',
      acceptLabel: 'Očisti',
      rejectLabel: 'Otkaži',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await attendanceAPI.cleanupOrphanedRecords();
          showCustomAlert.success('Napušteni zapisi su očišćeni');
          refresh();
        } catch (e: any) {
          showCustomAlert.error(e?.response?.data?.message || 'Greška pri čišćenju zapisa');
        }
      }
    });
  };

  const rowClassName = (r: RowState) => {
    if (r.isHoliday) return 'bg-yellow-50';
    if (r.isSickLeave) return 'bg-red-50';
    if (r.isAnnualLeave) return 'bg-blue-50';
    if (!r.recordId) return 'bg-orange-50';
    return 'bg-green-50';
  };

  const statusBody = (r: RowState) => {
    const label = r.locked ? 'Sačuvano' : 'Čeka sačuvaj';
    const cls = r.locked ? 'bg-green-500' : 'bg-orange-500';
    return (
      <span className={`inline-flex align-items-center gap-2 px-3 py-1 text-white text-sm border-round ${cls}`}>
        <i className={`pi ${r.locked ? 'pi-check' : 'pi-clock'}`} />
        {label}
      </span>
    );
  };

  const positionBody = (r: RowState) => {
    const pos = r.worker.positionId;
    if (typeof pos === 'object' && pos) return (pos as any).name || '';
    return '';
  };

  const workerBody = (r: RowState) => `${r.worker.lastName} ${r.worker.firstName}`;

  const hoursBody = (r: RowState) => {
    const disabled = r.locked || r.isAnnualLeave || r.isSickLeave || r.isHoliday;
    return (
      <div className="flex align-items-center gap-2">
        <Button
          icon="pi pi-minus"
          rounded
          text
          disabled={disabled}
          onClick={() => updateRow(r.worker._id, { hoursWorked: clamp(r.hoursWorked - 1, 0, 24), dirty: true })}
        />
        <InputNumber
          value={r.hoursWorked}
          disabled={disabled}
          min={0}
          max={24}
          inputStyle={{ width: 70, textAlign: 'center' }}
          onValueChange={(e) => updateRow(r.worker._id, { hoursWorked: e.value ?? 0, dirty: true })}
        />
        <Button
          icon="pi pi-plus"
          rounded
          text
          disabled={disabled}
          onClick={() => updateRow(r.worker._id, { hoursWorked: clamp(r.hoursWorked + 1, 0, 24), dirty: true })}
        />
      </div>
    );
  };

  const overtimeBody = (r: RowState) => {
    const disabled = r.locked || r.isAnnualLeave || r.isSickLeave || r.isHoliday;
    return (
      <div className="flex align-items-center gap-2">
        <Button
          icon="pi pi-minus"
          rounded
          text
          disabled={disabled}
          onClick={() => updateRow(r.worker._id, { overtimeHours: clamp(r.overtimeHours - 0.5, 0, 24), dirty: true })}
        />
        <InputNumber
          value={r.overtimeHours}
          disabled={disabled}
          min={0}
          max={24}
          step={0.5}
          minFractionDigits={1}
          maxFractionDigits={1}
          suffix=" h"
          inputStyle={{ width: 90, textAlign: 'center' }}
          onValueChange={(e) => updateRow(r.worker._id, { overtimeHours: e.value ?? 0, dirty: true })}
        />
        <Button
          icon="pi pi-plus"
          rounded
          text
          disabled={disabled}
          onClick={() => updateRow(r.worker._id, { overtimeHours: clamp(r.overtimeHours + 0.5, 0, 24), dirty: true })}
        />
      </div>
    );
  };

  const annualBody = (r: RowState) => {
    const disabled = r.locked || r.isHoliday;
    return (
      <Checkbox
        checked={r.isAnnualLeave}
        disabled={disabled}
        onChange={(e) => handleToggleAnnual(r, !!e.checked)}
      />
    );
  };

  const sickBody = (r: RowState) => {
    const disabled = r.locked || r.isHoliday;
    return (
      <Checkbox
        checked={r.isSickLeave}
        disabled={disabled}
        onChange={(e) => handleToggleSick(r, !!e.checked)}
      />
    );
  };

  const notesBody = (r: RowState) => {
    const disabled = r.locked || r.isHoliday;
    return (
      <InputText
        value={r.notes}
        disabled={disabled}
        placeholder="Napomene..."
        onChange={(e) => updateRow(r.worker._id, { notes: e.target.value, dirty: true })}
        className="w-full"
      />
    );
  };

  const actionBody = (r: RowState) => {
    if (r.locked) {
      return (
        <Button
          icon="pi pi-lock"
          rounded
          severity="warning"
          disabled={r.cannotUnlock}
          tooltip={r.cannotUnlock ? 'Zaključano zbog perioda/praznika' : 'Otključaj za izmenu'}
          tooltipOptions={{ position: 'left' }}
          onClick={() => unlockRow(r)}
        />
      );
    }
    return (
      <Button
        icon="pi pi-save"
        rounded
        severity="success"
        loading={r.saving}
        tooltip="Sačuvaj"
        tooltipOptions={{ position: 'left' }}
        onClick={() => saveRow(r)}
      />
    );
  };

  return (
    <div className="p-4">
      <Card>
        <div className="flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <h2 className="m-0">Dnevna evidencija prisustva</h2>

          <div className="flex align-items-center gap-2 flex-wrap">
            <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={refresh} />
            <Button label="Očisti napuštene zapise" icon="pi pi-trash" severity="danger" onClick={cleanupOrphaned} />
            <Button label="Sačuvaj sve" icon="pi pi-check" severity="success" onClick={saveAll} />
          </div>
        </div>

        <div className="flex align-items-center gap-3 mb-3">
          <div className="flex align-items-center gap-2">
            <div className="font-semibold">Datum:</div>
            <Calendar value={date} onChange={(e) => setDate(e.value as Date)} dateFormat="dd.mm.yy" showIcon />
          </div>
        </div>

        <div className="mb-3">
          <span className="p-input-icon-left w-full">
            <i className="pi pi-search" />
            <InputText
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pretraži radnike po imenu, prezimenu ili ID-u..."
              className="w-full"
            />
          </span>
        </div>

        <DataTable
          value={filteredRows}
          loading={loading}
          paginator
          rows={25}
          rowsPerPageOptions={[10, 25, 50]}
          currentPageReportTemplate="Prikaz {first} do {last} od ukupno {totalRecords} radnika"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          emptyMessage="Nema radnika"
          className="p-datatable-sm"
          rowClassName={rowClassName}
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column header="Radnik" body={workerBody} sortable sortField="worker.lastName" style={{ minWidth: 220 }} />
          <Column header="Pozicija" body={positionBody} sortable style={{ minWidth: 140 }} />
          <Column header="Radni sati" body={hoursBody} style={{ minWidth: 170 }} />
          <Column header="Prekovremeni" body={overtimeBody} style={{ minWidth: 190 }} />
          <Column header="Godišnji" body={annualBody} style={{ width: 90, textAlign: 'center' }} />
          <Column header="Bolovanje" body={sickBody} style={{ width: 100, textAlign: 'center' }} />
          <Column header="Napomene" body={notesBody} style={{ minWidth: 220 }} />
          <Column header="Status" body={statusBody} style={{ width: 140 }} />
          <Column header="" body={actionBody} style={{ width: 90 }} />
        </DataTable>

        <div className="mt-4">
          <div className="font-semibold mb-2">Legenda:</div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex px-3 py-1 border-round text-white bg-green-500">Radio - Normalni radni dan</span>
            <span className="inline-flex px-3 py-1 border-round text-white bg-red-500">Bolovanje - Radnik je na bolovanju</span>
            <span className="inline-flex px-3 py-1 border-round text-white bg-blue-500">Godišnji - Radnik je na godišnjem odmoru</span>
            <span className="inline-flex px-3 py-1 border-round text-white bg-orange-500">Bez evidencije - Još nije uneta evidencija</span>
          </div>
          <div className="mt-2 text-600">
            Napomena: Prekovremeni sati se unose u intervalima od 0.5 sata (30 minuta). Minimum za prekovremeni rad je 0.5 sata.
          </div>
        </div>
      </Card>

      <ConfirmDialog />
    </div>
  );
};

export default AttendanceDaily;


